// Sugestii - recomandari de module si cariere pentru elevi

// normalizeaza textul pentru potriviri pe cuvinte cheie
export const normalizeText = (s = '') =>
  String(s)
    .toLowerCase()
    .replace(/ă/g, 'a')
    .replace(/â/g, 'a')
    .replace(/î/g, 'i')
    .replace(/ș/g, 's')
    .replace(/ţ/g, 't')
    .replace(/ț/g, 't');

// hash FNV-1a pentru rotatie deterministica pe elev
const hashSeed = (seed = '') => {
  let h = 2166136261;
  const s = String(seed);
  for (let i = 0; i < s.length; i++) {
    h ^= s.charCodeAt(i);
    h = Math.imul(h, 16777619);
  }
  return h >>> 0;
};

// recomandare de profil dupa specializare
export const PROFILE_RECOMMENDATIONS = {
  MI: {
    profil: 'Tehnologic — Matematică-Informatică',
    descriere:
      'Aptitudini dezvoltate în logică, algoritmică și matematică aplicată. Direcții potrivite: tehnologie și științe exacte.',
    domenii: ['Dezvoltare software', 'Inginerie IT', 'Analiza datelor', 'Cibernetică'],
  },
  SN: {
    profil: 'Real — Științe ale Naturii',
    descriere:
      'Aptitudini dezvoltate în științe experimentale și exacte. Direcții potrivite: sănătate, biologie și științe aplicate.',
    domenii: ['Medicină', 'Biologie', 'Chimie aplicată', 'Farmacie', 'Inginerie'],
  },
  SS: {
    profil: 'Științe Sociale',
    descriere:
      'Aptitudini dezvoltate în analiza socială, comunicare și cadrul legal. Direcții potrivite: drept, psihologie și administrație.',
    domenii: ['Drept', 'Psihologie', 'Sociologie', 'Administrație publică'],
  },
  FL: {
    profil: 'Uman — Filologie',
    descriere:
      'Aptitudini dezvoltate în limbă, literatură și comunicare interculturală. Direcții potrivite: filologie, media și relații internaționale.',
    domenii: ['Filologie', 'Traduceri și interpretariat', 'Jurnalism', 'Relații internaționale'],
  },
  BU: {
    profil: 'Business — Economic',
    descriere:
      'Aptitudini dezvoltate în economie, organizare și management. Direcții potrivite: finanțe, marketing și antreprenoriat.',
    domenii: ['Management', 'Marketing', 'Finanțe-Bănci', 'Antreprenoriat'],
  },
};

export const getProfileRecommendation = (specializare) =>
  PROFILE_RECOMMENDATIONS[specializare] || null;

// module unde elevul mai are nevoie de credite; options: excludeNames, seed
export const getModuleGaps = (moduleCredits = [], activities = [], options = {}) => {
  const { excludeNames = [], seed = '' } = options || {};
  const excluded = new Set((excludeNames || []).map((n) => normalizeText(n)));
  const actsByModule = {};
  (activities || []).forEach((a) => {
    if (!actsByModule[a.id_module]) actsByModule[a.id_module] = [];
    actsByModule[a.id_module].push(a);
  });

  const seedHash = hashSeed(seed);

  return (moduleCredits || [])
    .filter((m) => m.tinta > 0)
    .map((m) => {
      const total = m.total || 0;
      const gap = Math.max(0, m.tinta - total);
      const progres = Math.min(100, Math.round((total / m.tinta) * 100));
      const pool = (actsByModule[m.id] || []).filter(
        (a) => !excluded.has(normalizeText(a.nume_activitate))
      );
      const sorted = [...pool].sort(
        (a, b) =>
          (b.ore_recomandate || 0) - (a.ore_recomandate || 0) ||
          a.nume_activitate.localeCompare(b.nume_activitate)
      );
      const offset = seed && sorted.length > 0 ? seedHash % sorted.length : 0;
      const rotated = offset > 0
        ? [...sorted.slice(offset), ...sorted.slice(0, offset)]
        : sorted;
      const activitati = rotated
        .slice(0, 3)
        .map((a) => ({ id: a.id, nume: a.nume_activitate, id_tip_credit: a.id_tip_credit }));

      return { id: m.id, cod: m.cod, nume: m.nume, total, tinta: m.tinta, gap, progres, activitati };
    })
    .filter((m) => m.gap > 0)
    .sort((a, b) => b.gap - a.gap || a.cod.localeCompare(b.cod));
};

// sugestii de cariera; potrivire pe cod + nume normalizate
export const CAREER_KEYWORDS = [
  {
    keywords: ['informatic', 'programar', 'software', 'web', 'tehnolog', 'digital', 'algoritm', 'baze de date'],
    domenii: ['Dezvoltare software', 'Inginerie IT', 'Analiza datelor'],
  },
  {
    keywords: ['medic', 'biolog', 'chimie', 'sanatate', 'natura', 'stiinte', 'fizic'],
    domenii: ['Medicină', 'Bioinginerie', 'Farmacie', 'Științele mediului'],
  },
  {
    keywords: ['economic', 'business', 'finant', 'marketing', 'antreprenor', 'management', 'contabil'],
    domenii: ['Finanțe', 'Marketing', 'Management', 'Antreprenoriat'],
  },
  {
    keywords: ['drept', 'social', 'psiholog', 'comunicare', 'jurnalism', 'umanist', 'filologie', 'limb'],
    domenii: ['Drept', 'Psihologie', 'Comunicare', 'Media'],
  },
  {
    keywords: ['inginer', 'matematic', 'tehnic', 'construct', 'mecanic', 'electric'],
    domenii: ['Inginerie', 'Matematică aplicată', 'Cercetare'],
  },
  {
    keywords: ['art', 'design', 'creativ', 'muzic', 'literatur', 'media'],
    domenii: ['Design', 'Arte vizuale', 'Media și producție'],
  },
];

export const getCareerSuggestions = (profileRec = null, moduleCredits = []) => {
  const rankedModules = (moduleCredits || [])
    .filter((m) => (m.total || 0) > 0)
    .sort((a, b) => b.total - a.total);

  const matched = {};
  rankedModules.forEach((m) => {
    const text = normalizeText(`${m.cod} ${m.nume}`);
    CAREER_KEYWORDS.forEach((group) => {
      const hit = group.keywords.some((kw) => text.includes(kw));
      if (!hit) return;
      group.domenii.forEach((d) => {
        if (!matched[d]) matched[d] = { domeniu: d, surse: [] };
        if (!matched[d].surse.includes(m.cod)) matched[d].surse.push(m.cod);
      });
    });
  });

  const fromModules = Object.values(matched);
  if (fromModules.length > 0) return fromModules.slice(0, 4);

  // fallback pe domeniile profilului
  if (profileRec && Array.isArray(profileRec.domenii)) {
    return profileRec.domenii.slice(0, 4).map((d) => ({ domeniu: d, surse: [] }));
  }
  return [];
};
